import React, { useState, useEffect } from 'react';
import { CreditCard, AlertCircle, RefreshCw } from 'lucide-react';
import { PaymentMethodBadge } from '@/components/AcceptedPaymentMethods';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';

export interface PaymentMethod {
  id: number;
  last4: string;
  brand: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
  expired: boolean;
}

interface PaymentMethodListProps {
  onAddCard: () => void;
  onSelect?: (paymentMethod: PaymentMethod) => void;
  selectedId?: number;
  refreshTrigger?: number;
}

const PaymentMethodList: React.FC<PaymentMethodListProps> = ({
  onAddCard,
  onSelect,
  selectedId,
  refreshTrigger = 0
}) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingMethod, setDeletingMethod] = useState<PaymentMethod | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchPaymentMethods = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get<{ payment_methods: PaymentMethod[] }>(
        '/customer/payment_methods'
      );
      setPaymentMethods(response.data.payment_methods);
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
      toast.error('カード情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, [refreshTrigger]);

  // 登録カードを自動選択（1枚制限なので最初のカードを自動選択）
  useEffect(() => {
    if (paymentMethods.length > 0 && onSelect) {
      onSelect(paymentMethods[0]);
    }
  }, [paymentMethods, onSelect]);

  const handleDeleteClick = (method: PaymentMethod) => {
    setDeletingMethod(method);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingMethod) return;

    setDeleting(true);
    try {
      await apiClient.delete(`/customer/payment_methods/${deletingMethod.id}`);
      toast.success('カードを削除しました');
      setDeleteDialogOpen(false);
      setDeletingMethod(null);
      fetchPaymentMethods();
      // カード削除後は選択を解除
      if (onSelect) {
        onSelect(null as any);
      }
    } catch (error) {
      console.error('Failed to delete payment method:', error);
      toast.error('カードの削除に失敗しました');
    } finally {
      setDeleting(false);
    }
  };

  const getCardBrandIcon = (brand: string) => {
    return <PaymentMethodBadge method={brand} size="md" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // カード未登録の場合
  if (paymentMethods.length === 0) {
    return (
      <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center">
        <CreditCard className="h-10 w-10 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 mb-4 text-sm">カードが登録されていません</p>
        <Button onClick={onAddCard} size="sm">
          <CreditCard className="h-4 w-4 mr-2" />
          カードを登録
        </Button>
      </div>
    );
  }

  // カード登録済みの場合（1枚のみ表示）
  const method = paymentMethods[0];

  return (
    <>
      <div className="space-y-3">
        {/* カード情報表示 */}
        <div
          className={`border rounded-lg p-4 ${
            method.expired
              ? 'border-red-300 bg-red-50'
              : 'border-gray-200 bg-gray-50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center">
                {getCardBrandIcon(method.brand)}
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">
                    {method.brand} •••• {method.last4}
                  </span>
                  {method.expired && (
                    <Badge variant="destructive" className="text-xs">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      期限切れ
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  有効期限: {String(method.exp_month).padStart(2, '0')}/{method.exp_year}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* カード変更ボタン */}
        <Button
          variant="outline"
          size="sm"
          onClick={onAddCard}
          className="w-full"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          カードを変更
        </Button>

        {/* カード削除リンク */}
        <div className="text-center">
          <button
            onClick={() => handleDeleteClick(method)}
            className="text-xs text-gray-400 hover:text-red-500 transition-colors"
          >
            カードを削除
          </button>
        </div>
      </div>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>カードを削除しますか？</DialogTitle>
            <DialogDescription>
              {deletingMethod && (
                <>
                  {deletingMethod.brand} •••• {deletingMethod.last4} を削除します。
                  <br />
                  この操作は取り消せません。
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              キャンセル
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? '削除中...' : '削除する'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentMethodList;
